import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { formatMoney } from "@/components/helpers/string-utils";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { ProductCategories } from "@/graphql";
import { PendingOrder } from "@/types/products";
import { ensureNonNullArray } from "@/utils/array";
import { formatCredits, parseDecimal } from "@/utils/string";

interface TotalDisplayProps {
  amount?: string | null;
  paymentType: string;
}

function TotalDisplay({ amount, paymentType }: TotalDisplayProps) {
  if (paymentType === "FIAT") {
    return formatMoney({ cashflow: amount });
  }
  return (
    <Flex alignItems="center">
      {parseDecimal(amount) < 0 ? <Box>-</Box> : null}
      <EmbedSvg src={CreditSVG} height={16} width={16} />
      <Box ml={1}>{formatCredits(amount)}</Box>
    </Flex>
  );
}

interface OrderDetailTableProps {
  order: PendingOrder;
  paymentType: string;
}

export function OrderDetailTable({
  order,
  paymentType,
}: OrderDetailTableProps) {
  if (!order) return null;

  const orderItems = ensureNonNullArray(order.orderItems);
  const orderGrossTotal =
    paymentType === "FIAT"
      ? order.grossTotal && order.grossTotal !== "0.00"
        ? order.grossTotal
        : order.grossCreditTotalInDollars
      : order.grossCreditTotal;

  const orderTotal =
    paymentType === "FIAT"
      ? order.total && order.total !== "0.00"
        ? order.total
        : order.creditTotalInDollars
      : order.creditTotal;

  const orderDiscountTotal =
    paymentType === "FIAT"
      ? order.promoDiscount && order.promoDiscount !== 0
        ? order.promoDiscount
        : order.promoCreditTotalInDollarsDiscount
      : order.promoCreditDiscount;

  return (
    <>
      {orderItems.map((item) => {
        const itemGrossTotal =
          paymentType === "FIAT"
            ? item.grossTotal && item.grossTotal !== "0.00"
              ? item.grossTotal
              : item.grossCreditTotalInDollars
            : item.grossCreditTotal;

        const itemTotal =
          paymentType === "FIAT"
            ? item.total && item.total !== "0.00"
              ? item.total
              : item.creditTotalInDollars
            : item.creditTotal;

        const itemDiscountTotal =
          paymentType === "FIAT"
            ? item.promoDiscount && item.promoDiscount !== "0.00"
              ? item.promoDiscount
              : item.promoCreditTotalInDollarsDiscount
            : item.promoCreditDiscount;

        const fiatCreditLabel =
          paymentType === "FIAT" &&
          item.product?.category !== ProductCategories.ExtraCredits &&
          item.product?.category !== ProductCategories.Credits
            ? ` (${formatCredits(item.creditTotal)})`
            : "";

        return (
          <div key={item.id}>
            <Flex mb={2} justifyContent="space-between" flexWrap="wrap">
              <Box fontSize={18}>
                {item.product?.name || item.product?.slug}
                {fiatCreditLabel}
              </Box>
              <Flex alignItems="center">
                {parseDecimal(itemGrossTotal) !== parseDecimal(itemTotal) ? (
                  <Box
                    mr={2}
                    color={"rgba(119, 131, 151, 0.6)"}
                    style={{ textDecoration: "line-through" }}
                  >
                    <TotalDisplay
                      amount={itemGrossTotal}
                      paymentType={paymentType}
                    />
                  </Box>
                ) : null}
                {parseDecimal(itemTotal) !== 0 ? (
                  <Box>
                    <TotalDisplay
                      amount={itemTotal}
                      paymentType={paymentType}
                    />
                  </Box>
                ) : null}
              </Flex>
            </Flex>
            {parseDecimal(itemDiscountTotal) !== 0 ? (
              <Flex
                mb={2}
                justifyContent="space-between"
                key={item.id}
                flexWrap="wrap"
              >
                <Box fontSize={14} ml={3}>
                  Promo Code: "{order.promoCode?.code}"
                </Box>
                <Box>
                  <TotalDisplay
                    amount={`-${itemDiscountTotal}`}
                    paymentType={paymentType}
                  />
                </Box>
              </Flex>
            ) : null}
          </div>
        );
      })}
      {orderDiscountTotal ? (
        <Flex mb={2} justifyContent="space-between" flexWrap="wrap">
          <Box fontSize={18}>Promo Code: "{order.promoCode?.code}"</Box>
          <Box>
            <TotalDisplay
              amount={`-${orderDiscountTotal}`}
              paymentType={paymentType}
            />
          </Box>
        </Flex>
      ) : null}

      <Flex
        mt={2}
        pt={2}
        className="bts"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Box fontWeight={500} fontSize={18}>
          Order Total
        </Box>
        <Flex alignItems="center">
          {orderGrossTotal !== orderTotal ? (
            <Box
              mr={2}
              fontWeight="600"
              color={"rgba(119, 131, 151, 0.6)"}
              style={{ textDecoration: "line-through" }}
            >
              <TotalDisplay
                amount={orderGrossTotal}
                paymentType={paymentType}
              />
            </Box>
          ) : null}
          <Box fontWeight={600}>
            <TotalDisplay amount={orderTotal} paymentType={paymentType} />
          </Box>
        </Flex>
      </Flex>
    </>
  );
}
